// Theme — the app's ONE global palette seam.
//
// tokens.css defines the dark palette under `[data-theme="dark"]` on a root
// ancestor; Storybook stamps that attribute via its toolbar decorator so the
// workbench can preview dark. The assembled app had no equivalent: nothing
// stamped `data-theme` on the document root, so choosing Dark in Settings only
// re-themed the Settings pane's own subtree while the rest of the app stayed
// light. This provider closes that gap by owning the ONE place the whole app's
// palette is applied.
//
// PER-DEVICE, OUTSIDE THE EVENT LOG. The colour theme is a device preference,
// not a domain fact (memory project_theme_outside_event_log): it is persisted
// to localStorage and applied to `document.documentElement`, never emitted as
// an event. This is a legitimate impure-shell composition boundary (a mount
// provider) — it may hold React state and run the ONE sanctioned DOM/storage
// effect; every consumer below it stays a pure view reading `useTheme()`.
//
// SYSTEM semantics: "system" removes the attribute, so the app falls back to
// the light default baked into tokens.css. We deliberately do NOT infer the OS
// palette from `prefers-color-scheme` — tokens.css documents "dark is opt-in,
// never OS-inferred, so the app has one predictable starting state", and that
// decision is the owner's to revisit. So today "system" == light. The
// Preferences UI no longer offers "system" as a pick (it would be a third
// control position with no observable effect — settings-simplification-
// 2026-09-22.md §12); the seam still accepts it so a stored value from before
// this change, or a future re-introduction, both just work. Explicit
// Light/Dark apply globally.

import {
    createContext,
    type ReactNode,
    useContext,
    useEffect,
    useState,
} from "react";
import type { ThemePreference } from "../components/Settings/types.js";
import { readDeviceKey, writeDeviceKey } from "./deviceStore.js";

// The localStorage key the device preference round-trips through. Namespaced so
// it never collides with another app's key on a shared origin.
const STORAGE_KEY = "bilda.theme";

// The palette the app ACTUALLY paints under a given preference. "system" and
// "light" both render light today (tokens.css bakes light as the default and
// dark is opt-in, never OS-inferred — see the header note); only "dark" paints
// dark. The quick-toggle reads this to pick the OPPOSITE explicit palette.
export const effectiveTheme = (theme: ThemePreference): "light" | "dark" =>
    theme === "dark" ? "dark" : "light";

// Parse, don't validate: localStorage hands back `string | null` (untrusted).
// Land it in the `ThemePreference` union at the boundary; anything unexpected
// (absent, corrupted, a value from a future build) degrades to "light" — the
// default a fresh device starts on (the Preferences control no longer offers
// "system" to pick, per .claude/analysis/settings-simplification-2026-09-22.md
// §12). A device with an ALREADY-persisted "system" value still parses and
// renders as light (`effectiveTheme` below) — no migration, no behaviour
// change for existing users.
const parsePreference = (raw: string | null): ThemePreference =>
    raw === "light" || raw === "dark" || raw === "system" ? raw : "light";

// The live device preference plus the one setter that changes it. A pure view
// reads `theme` to show the current position; the Settings toggle calls
// `setTheme` to switch the whole app.
export type ThemeContextValue = {
    readonly theme: ThemePreference;
    readonly setTheme: (theme: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

// Consume the global theme. Throws if used outside the provider — a wiring bug
// surfaced loudly at first use rather than silently no-oping (mirrors
// `useAppPorts`).
export const useTheme = (): ThemeContextValue => {
    const ctx = useContext(ThemeContext);
    if (ctx === null) {
        throw new Error(
            "Theme not provided — mount must wrap the app in ThemeProvider",
        );
    }
    return ctx;
};

// The impure boundary. Holds the preference in React state (seeded once from
// the persisted value), and runs the single effect that reflects it onto the
// document root and persists it. `data-theme` is stamped on <html> so it covers
// the ENTIRE app — nav rail, toolbar, material canvas, chat, body — not just a
// nested subtree.
export const ThemeProvider = ({
    children,
}: {
    readonly children: ReactNode;
}) => {
    // Defaults to "light" (parsePreference on an unset key) — a fresh device
    // starts on the light palette, never gated by a cold-open colour question
    // (the trust lens flagged that as a "consumer toy" signal). The
    // preference is changed from Settings › Appearance, never at first paint.
    const [theme, setTheme] = useState<ThemePreference>(() =>
        parsePreference(readDeviceKey(STORAGE_KEY)),
    );

    useEffect(() => {
        const root = document.documentElement;
        // system → no attribute → the light default baked into tokens.css.
        if (theme === "system") root.removeAttribute("data-theme");
        else root.setAttribute("data-theme", theme);
        writeDeviceKey(STORAGE_KEY, theme);
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};
